import { Link } from "react-router-dom";
import { footerData, LOGO_URL, INSTAGRAM, EMAIL, DPIIT, SERVICE_PAGES } from "../data/mock";
import { Instagram, MessageCircle, ArrowUp, Award } from "lucide-react";
import { Separator } from "./ui/separator";
import { useStorePicker } from "../hooks/useStorePicker";

export const Footer = () => {
  const { open: openStorePicker } = useStorePicker();
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  return (
    <footer className="bg-slate-900 text-white relative">
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 pt-20 pb-12">
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-10 lg:gap-8">
          {/* About */}
          <div className="lg:col-span-2">
            <Link to="/" className="flex items-center gap-1 mb-5">
              <img src={LOGO_URL} alt="Blitzwash" className="h-14 w-auto object-contain brightness-110" />
            </Link>
            <p className="text-sm text-slate-400 leading-relaxed mb-6 max-w-sm">
              {footerData.description}
            </p>
            <div className="flex items-center gap-3 mb-6">
              <a href={INSTAGRAM} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-xl bg-slate-800 hover:bg-gradient-to-br hover:from-pink-500 hover:to-orange-400 flex items-center justify-center transition-all duration-300" data-testid="footer-instagram-btn" aria-label="Instagram">
                <Instagram className="w-4 h-4" />
              </a>
              <button type="button" onClick={() => openStorePicker("whatsapp")} className="w-10 h-10 rounded-xl bg-slate-800 hover:bg-green-500 flex items-center justify-center transition-colors duration-300" aria-label="WhatsApp" data-testid="footer-whatsapp-btn">
                <MessageCircle className="w-4 h-4" />
              </button>
            </div>
            {/* DPIIT badge */}
            <div className="inline-flex items-center gap-2.5 px-3.5 py-2 rounded-xl bg-slate-800/50 border border-slate-700" data-testid="footer-dpiit-badge">
              <Award className="w-5 h-5 text-orange-400 flex-shrink-0" />
              <div>
                <p className="text-[10px] font-bold text-white uppercase tracking-wider leading-tight">{DPIIT.label}</p>
                <p className="text-[10px] text-slate-400 leading-tight">#{DPIIT.number}</p>
              </div>
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-sm font-semibold text-white uppercase tracking-wider mb-5">Services</h4>
            <ul className="space-y-3">
              {Object.entries(SERVICE_PAGES).map(([slug, p]) => (
                <li key={slug}>
                  <Link to={`/services/${slug}`} className="text-sm text-slate-400 hover:text-sky-400 transition-colors duration-200">
                    {p.title.replace(" in Jaipur", "")}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-sm font-semibold text-white uppercase tracking-wider mb-5">Company</h4>
            <ul className="space-y-3">
              <li><Link to="/about" className="text-sm text-slate-400 hover:text-sky-400 transition-colors">About Us</Link></li>
              <li><Link to="/pricing" className="text-sm text-slate-400 hover:text-sky-400 transition-colors">Pricing</Link></li>
              <li><Link to="/corporate" className="text-sm text-slate-400 hover:text-sky-400 transition-colors">Corporate / B2B</Link></li>
              <li><Link to="/blog" className="text-sm text-slate-400 hover:text-sky-400 transition-colors">Blog</Link></li>
              <li><Link to="/#stores" className="text-sm text-slate-400 hover:text-sky-400 transition-colors">Our Stores</Link></li>
              <li><Link to="/#faq" className="text-sm text-slate-400 hover:text-sky-400 transition-colors">FAQ</Link></li>
              <li><Link to="/privacy-policy" className="text-sm text-slate-400 hover:text-sky-400 transition-colors">Privacy Policy</Link></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-sm font-semibold text-white uppercase tracking-wider mb-5">Contact</h4>
            <ul className="space-y-4">
              <li>
                <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Phone / WhatsApp</p>
                <button type="button" onClick={() => openStorePicker("call")} className="text-sm text-slate-300 hover:text-sky-400 transition-colors block" data-testid="footer-call-btn">Tap to pick a store</button>
              </li>
              <li>
                <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Email</p>
                <a href={`mailto:${EMAIL}`} className="text-sm text-slate-300 hover:text-sky-400 transition-colors">{EMAIL}</a>
              </li>
              <li>
                <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Instagram</p>
                <a href={INSTAGRAM} target="_blank" rel="noopener noreferrer" className="text-sm text-slate-300 hover:text-sky-400 transition-colors">@blitzwashlaundry</a>
              </li>
              <li>
                <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Location</p>
                <p className="text-sm text-slate-300">Jaipur, Rajasthan</p>
              </li>
            </ul>
          </div>
        </div>

        <Separator className="my-10 bg-slate-800" />

        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div>
            <p className="text-sm text-slate-500">
              {"\u00A9"} {new Date().getFullYear()} Blitzwash Laundry. All rights reserved.
            </p>
            <p className="text-xs text-slate-600 mt-1">Currently serving Jaipur. Soon bringing the magic to other parts of India.</p>
          </div>
          <button onClick={scrollToTop} className="w-10 h-10 rounded-xl bg-slate-800 hover:bg-sky-600 flex items-center justify-center transition-colors duration-300" aria-label="Back to top">
            <ArrowUp className="w-4 h-4" />
          </button>
        </div>
      </div>
    </footer>
  );
};
